import { useCallback, useEffect, useState } from 'react';
import { Banner, Text } from '@shopify/polaris';
import { useApi } from '../providers/ApiProvider';
import { openInNewBrowserTab } from '../lib/shopifyNav';
import { themeEmbedDeepLink } from '../lib/shopContext';
import { t } from '../i18n';

interface AppActivation {
  active: boolean;
  deepLink: string;
  blockName?: string;
  shopDomain?: string;
}

/**
 * Warning shown only when Freebi is not activated on the storefront theme.
 * Merchant-facing copy avoids "app embed" jargon.
 */
export function ActivateAppBanner() {
  const api = useApi();
  const [activation, setActivation] = useState<AppActivation | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await api.get<{
        shopDomain?: string;
        appEmbed: {
          active: boolean;
          deepLink: string;
          blockName?: string;
        };
      }>('/stores/setup');
      setActivation({
        active: Boolean(s.appEmbed?.active),
        deepLink: s.appEmbed?.deepLink || '#',
        blockName: s.appEmbed?.blockName,
        shopDomain: s.shopDomain,
      });
    } catch {
      // Fail open — don't block campaigns if setup check fails
      setActivation(null);
    }
  }, [api]);

  useEffect(() => {
    void load();
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    const id = window.setInterval(() => void load(), 12000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(id);
    };
  }, [load]);

  // Only warn when we know the app is not activated
  if (!activation || activation.active) return null;

  const link = themeEmbedDeepLink(activation.deepLink, activation.shopDomain);

  return (
    <Banner tone="warning" title={t("Activate Freebi on your store")}>
      <p>
        {t("Your campaigns won&apos;t show free gifts to customers until Freebi is turned on\n        in your theme. Enable")}{' '}
        <Text as="span" fontWeight="semibold">
          {activation.blockName || 'Storefront gifts'}
        </Text>
        {t(", then save.")}
      </p>
      <div style={{ marginTop: '12px' }}>
        {/* Real anchor: App Bridge rewrites window.open for admin URLs to same window */}
        <a
          href={link || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!link) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            setBusy(true);
            openInNewBrowserTab(link);
            window.setTimeout(() => {
              setBusy(false);
              void load();
            }, 500);
            window.setTimeout(() => void load(), 4000);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 16px',
            borderRadius: '8px',
            background: busy ? '#8c9196' : '#303030',
            color: '#fff',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 600,
            pointerEvents: busy ? 'none' : 'auto',
          }}
        >
          {busy ? 'Opening…' : 'Activate app'}
        </a>
      </div>
    </Banner>
  );
}
